/**
 * EditBuddyGroupPreferencesDialog — lets the primary cardholder change
 * the buddy group's performance preferences for ONE rodeo from the
 * buddy group management screen.
 *
 * Until now those values were read-only here: the only way to change
 * them was the rodeo's Buddy Group setup page, which members reach
 * through the rodeo, not through their group. The rules are unchanged
 * and still enforced server-side:
 *   - primary cardholder only (403 NOT_PRIMARY otherwise),
 *   - editable only until a draw is published/finalized for the rodeo
 *     (409 PREFERENCES_FROZEN afterwards),
 *   - the pair must satisfy the rodeo's `preference_config`.
 * Saving back-fills every group member's existing entries in that
 * rodeo — it does NOT touch pricing or partner invitations.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  PerformancePreferenceStep,
  type PerformanceChoice,
} from '@/components/events/PerformancePreferenceStep';
import { httpClient } from '@/api/http-client';
import { useToast } from '@/contexts/ToastContext';
import { formatInTimezone } from '@/utils/timezone';
import { useSetBuddyGroupPreferences } from '@/hooks/useBuddyGroupPreferences';

interface PreferenceConfig {
  ifPerformance: ('slack' | 'out' | 'performance')[];
  ifSlack: ('slack' | 'out' | 'performance')[];
}

interface PerformanceRow {
  id: string;
  performance_name: string | null;
  is_slack: boolean | null;
  start_datetime: string | null;
}

const DEFAULT_PERFORMANCE_NAMES = ['Performance 1', 'Performance 2', 'Performance 3'];

/** Strip the disambiguation `[<isoDatetime>]` suffix stored values carry. */
function preferenceHead(value: string | null | undefined): string {
  if (!value) return '';
  return value.trim().replace(/\s*\[[^\]]*\]\s*$/, '');
}

/**
 * Promote a stored value ("Performance 1", "Slack") to the matching
 * option in the freshly built list ("Performance 1 [<iso>]") so the
 * dropdown shows the group's current pick instead of going blank.
 */
function resolveStoredPreference(
  stored: string | null | undefined,
  options: PerformanceChoice[],
): string {
  if (!stored) return '';
  if (options.some((o) => o.value === stored)) return stored;
  const head = preferenceHead(stored);
  if (!head) return stored;
  const match = options.find((o) => preferenceHead(o.value) === head);
  return match ? match.value : stored;
}

interface EditBuddyGroupPreferencesDialogProps {
  open: boolean;
  onClose: () => void;
  groupId: string;
  rodeoId: string;
  /** Rodeo display name for the dialog copy. */
  rodeoName?: string | null;
  /** Rodeo's IANA timezone — anchors every dropdown label. */
  timezone?: string | null;
  currentFirst: string | null;
  currentSecond: string | null;
}

export function EditBuddyGroupPreferencesDialog({
  open,
  onClose,
  groupId,
  rodeoId,
  rodeoName,
  timezone,
  currentFirst,
  currentSecond,
}: EditBuddyGroupPreferencesDialogProps) {
  const { showSuccess, showError } = useToast();
  const savePreferences = useSetBuddyGroupPreferences();

  const [first, setFirst] = useState<string>('');
  const [second, setSecond] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<PerformanceChoice[]>([]);
  const [preferenceConfig, setPreferenceConfig] =
    useState<PreferenceConfig | null>(null);

  const displayTz: string = timezone || '';

  // Seed the form from the group's current picks every time the dialog
  // opens so a half-finished edit from a previous open doesn't leak in.
  useEffect(() => {
    if (!open) return;
    setFirst(currentFirst ?? '');
    setSecond(currentSecond ?? '');
    setError(null);
  }, [open, currentFirst, currentSecond]);

  // Build the dropdown option list from the rodeo's performances and
  // pull the admin's preference gating. Mirrors BuddyGroupSetupPage so
  // the stored values round-trip identically between the two surfaces.
  useEffect(() => {
    if (!open || !rodeoId) return;
    let cancelled = false;

    (async () => {
      const [rows, rodeoRow] = await Promise.all([
        httpClient
          .get<{ data: PerformanceRow[] }>(
            `/performances?trig_rodeo_id=${rodeoId}&limit=50`,
          )
          .then((r) => r?.data ?? [])
          .catch(() => [] as PerformanceRow[]),
        httpClient
          .get<{ data: { preference_config: PreferenceConfig | null } }>(
            `/rodeos/${rodeoId}`,
          )
          .then((r) => r?.data ?? null)
          .catch(() => null),
      ]);
      if (cancelled) return;

      // De-dupe on (name, start_datetime): the unified `performances`
      // table repeats the same physical run once per event slot, and
      // those are one option, not several.
      const seen = new Set<string>();
      const built: PerformanceChoice[] = [...rows]
        .filter((p) => !!p.performance_name)
        .sort((a, b) => {
          const aTime = a.start_datetime ? Date.parse(a.start_datetime) : 0;
          const bTime = b.start_datetime ? Date.parse(b.start_datetime) : 0;
          return aTime - bTime;
        })
        .reduce<PerformanceChoice[]>((acc, p) => {
          const name = p.performance_name as string;
          const isSlack = !!p.is_slack;
          const isoDate = p.start_datetime ?? '';
          const dedupeKey = `${isSlack ? 'slack' : 'perf'}::${name}::${isoDate}`;
          if (seen.has(dedupeKey)) return acc;
          seen.add(dedupeKey);
          const baseValue = isSlack ? 'Slack' : name;
          const value = isoDate ? `${baseValue} [${isoDate}]` : baseValue;
          const dateLabel = isoDate
            ? formatInTimezone(isoDate, displayTz, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })
            : '';
          const labelName =
            isSlack && name.toLowerCase() === 'slack' ? 'Slack' : name;
          acc.push({
            value,
            label: dateLabel ? `${labelName} — ${dateLabel}` : labelName,
            isSlack,
          });
          return acc;
        }, []);

      const finalOptions: PerformanceChoice[] =
        built.length > 0
          ? built
          : DEFAULT_PERFORMANCE_NAMES.map((name) => ({
              value: name,
              label: name,
              isSlack: false,
            })).concat([{ value: 'Slack', label: 'Slack', isSlack: true }]);

      setOptions(finalOptions);
      setPreferenceConfig(rodeoRow?.preference_config ?? null);
      // Re-point the seeded values at the option that actually exists in
      // the list so the trigger renders the current pick.
      setFirst((prev) => resolveStoredPreference(prev, finalOptions));
      setSecond((prev) => resolveStoredPreference(prev, finalOptions));
    })();

    return () => {
      cancelled = true;
    };
  }, [open, rodeoId, displayTz]);

  const isDirty = useMemo(() => {
    const normalizedFirst = resolveStoredPreference(currentFirst, options);
    const normalizedSecond = resolveStoredPreference(currentSecond, options);
    return first !== normalizedFirst || second !== normalizedSecond;
  }, [first, second, currentFirst, currentSecond, options]);

  const submitting = savePreferences.isPending;

  const handleSubmit = async () => {
    setError(null);
    if (!first.trim()) {
      setError('Pick a 1st preference.');
      return;
    }
    try {
      await savePreferences.mutateAsync({
        groupId,
        rodeoId,
        preferences_first: first,
        preferences_second: second || null,
      });
      showSuccess(
        'Buddy group preferences updated',
        'Every group member’s entries for this rodeo now use these picks.',
      );
      onClose();
    } catch (err: any) {
      // Server messages are user-facing here — a draw that ran between
      // page load and submit comes back as a 409 with the reason.
      const message = err?.message ?? 'Failed to save preferences';
      setError(message);
      showError('Could not update preferences', message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submitting) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit group preferences</DialogTitle>
          <DialogDescription>
            {rodeoName
              ? `Set the performance preferences your whole buddy group runs on at ${rodeoName}. They apply to every member's entries in this rodeo.`
              : "Set the performance preferences your whole buddy group runs on. They apply to every member's entries in this rodeo."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <PerformancePreferenceStep
            performanceOptions={options.length > 0 ? options : undefined}
            performances={
              options.length === 0 ? DEFAULT_PERFORMANCE_NAMES : undefined
            }
            firstPreference={first}
            secondPreference={second}
            onFirstChange={setFirst}
            onSecondChange={setSecond}
            preferenceConfig={preferenceConfig}
            error={error ?? undefined}
            // Validation failures and server rejections (e.g. a draw
            // that ran mid-session) render inside the step, next to the
            // fields they concern.
            timezone={timezone ?? null}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !isDirty}>
            {submitting ? 'Saving…' : 'Save preferences'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
